/**
 * VenueFlow Context Provider
 * 
 * Provides user/venue context throughout the app.
 * Can be sourced from URL params, local storage, or auth system.
 */

import React, { createContext, useContext, useState, useEffect, useMemo } from "react";

// ============================================================
// CLUB MAPPINGS
// ============================================================
export const CLUBS = {
  ADE: { name: "Adelaide Crows", shortName: "ADE" },
  BRI: { name: "Brisbane Lions", shortName: "BRI" },
  CAR: { name: "Carlton", shortName: "CAR" },
  COL: { name: "Collingwood", shortName: "COL" },
  ESS: { name: "Essendon", shortName: "ESS" },
  FRE: { name: "Fremantle", shortName: "FRE" },
  GEE: { name: "Geelong Cats", shortName: "GEE" },
  GCS: { name: "Gold Coast Suns", shortName: "GCS" },
  GWS: { name: "GWS Giants", shortName: "GWS" },
  HAW: { name: "Hawthorn", shortName: "HAW" },
  MEL: { name: "Melbourne", shortName: "MEL" },
  NM: { name: "North Melbourne", shortName: "NM" },
  NOR: { name: "North Melbourne", shortName: "NOR" },
  PA: { name: "Port Adelaide", shortName: "PA" },
  RIC: { name: "Richmond", shortName: "RIC" },
  STK: { name: "St Kilda", shortName: "STK" },
  SYD: { name: "Sydney Swans", shortName: "SYD" },
  WCE: { name: "West Coast Eagles", shortName: "WCE" },
  WB: { name: "Western Bulldogs", shortName: "WB" },
  WBD: { name: "Western Bulldogs", shortName: "WBD" },
  // States (for commercial content)
  WA: { name: "Western Australia", shortName: "WA" },
  VIC: { name: "Victoria", shortName: "VIC" },
  // Neutral fallback (misconfiguration indicator)
  LUM: { name: "Lumen8 Media", shortName: "L8" },
};

// ============================================================
// CONTEXT DEFINITION
// ============================================================
const VFContext = createContext(null);

// ============================================================
// DEFAULT CONTEXT VALUES
// Read from environment variables for multi-club deployment
// ============================================================
const ENV_CLUB = process.env.REACT_APP_DEFAULT_CLUB || "LUM";
const ENV_VENUE = process.env.REACT_APP_DEFAULT_VENUE || "MCG";

const DEFAULT_CONTEXT = {
  submitterType: "Club",
  submitterName: CLUBS[ENV_CLUB]?.name || "Lumen8 Media",
  submitterShortName: CLUBS[ENV_CLUB]?.shortName || "L8",
  venue: ENV_VENUE,
};

// ============================================================
// LOCALSTORAGE KEY SCOPING
// Each deployment (stkilda.*, northmelbourne.*, hawthorn.*) has its own key,
// so a user visiting multiple portals in the same browser can't carry a stale
// club identity across subdomains. Also clears the legacy unscoped `vf_context`
// key on first run so existing users with stale values get a clean slate.
// ============================================================
const VF_CONTEXT_STORAGE_KEY = `vf_context_${ENV_CLUB}`;
const LEGACY_VF_CONTEXT_KEY = "vf_context";

function readStoredContext() {
  // Clean up legacy unscoped key if present
  try {
    if (localStorage.getItem(LEGACY_VF_CONTEXT_KEY) !== null) {
      localStorage.removeItem(LEGACY_VF_CONTEXT_KEY);
    }
  } catch {
    // Ignore — localStorage may be unavailable (SSR, private mode, etc.)
  }

  let stored;
  try {
    stored = localStorage.getItem(VF_CONTEXT_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!stored) return null;

  let parsed;
  try {
    parsed = JSON.parse(stored);
  } catch {
    return null;
  }

  // Guard: stored context must match this deployment's club.
  // If someone manually set a scoped key or the env var changed between builds,
  // discard the stored value rather than display the wrong club.
  const storedShortName = parsed?.submitterShortName;
  const expectedShortName = CLUBS[ENV_CLUB]?.shortName;
  if (expectedShortName && storedShortName && storedShortName !== expectedShortName) {
    try { localStorage.removeItem(VF_CONTEXT_STORAGE_KEY); } catch {}
    return null;
  }

  return parsed;
}

// ============================================================
// PARSE URL PARAMS
// ============================================================
function getContextFromURL() {
  const params = new URLSearchParams(window.location.search);
  
  const shortName = params.get("club") || params.get("shortName");
  const venue = params.get("venue");
  const submitterType = params.get("type") || "Club";
  
  if (shortName && CLUBS[shortName]) {
    return {
      submitterType,
      submitterName: CLUBS[shortName].name,
      submitterShortName: shortName,
      venue: venue || "MCG",
    };
  }
  
  return null;
}

// ============================================================
// PROVIDER COMPONENT
// ============================================================
export function VFContextProvider({ children, initialContext }) {
  const [context, setContext] = useState(() => {
    // Priority: initialContext > URL params > localStorage > defaults
    if (initialContext) return initialContext;
    
    const urlContext = getContextFromURL();
    if (urlContext) return urlContext;
    
    const stored = readStoredContext();
    if (stored) return stored;
    
    return DEFAULT_CONTEXT;
  });

  // Persist context changes
  useEffect(() => {
    try {
      localStorage.setItem(VF_CONTEXT_STORAGE_KEY, JSON.stringify(context));
    } catch {
      // Ignore — localStorage may be unavailable
    }
  }, [context]);

  // Derived values
  const submissionParent = useMemo(() => {
    return `/AFL_Content/_incoming/${context.submitterType}/${context.submitterName}`;
  }, [context.submitterType, context.submitterName]);

  const value = useMemo(
    () => ({
      ...context,
      submissionParent,
      setContext,
      // Convenience setters
      setVenue: (venue) => setContext((prev) => ({ ...prev, venue })),
      setClub: (shortName) => {
        if (CLUBS[shortName]) {
          setContext((prev) => ({
            ...prev,
            submitterName: CLUBS[shortName].name,
            submitterShortName: shortName,
          }));
        }
      },
    }),
    [context, submissionParent]
  );

  return <VFContext.Provider value={value}>{children}</VFContext.Provider>;
}

// ============================================================
// HOOK
// ============================================================
export function useVFContext() {
  const context = useContext(VFContext);
  if (!context) {
    throw new Error("useVFContext must be used within a VFContextProvider");
  }
  return context;
}

export default VFContext;
